"use client";

import { Bookmark, Heart } from "lucide-react";
import { FollowButton } from "@/components/profile/FollowButton";
import { MoreMenu } from "@/components/ui/MoreMenu";
import { useUi } from "@/components/ui/use-ui";
import type { VideoSummary } from "@/lib/api/types";
import { cn } from "@/lib/cn";
import { useVideoToggle, type VideoList } from "@/lib/library";
import { videoHref } from "./VideoCard";
import { VideoRating } from "./VideoRating";

/** `label`: the button's words off and on (lib/locales ui actions). */
const ACTIONS: { list: VideoList; label: ["react" | "save", "reacted" | "saved"]; Icon: typeof Heart; fill?: boolean }[] = [
  { list: "reacted", label: ["react", "reacted"], Icon: Heart, fill: true },
  { list: "saved", label: ["save", "saved"], Icon: Bookmark, fill: true },
];

/** Icon row to the right of the video title (thumbs up/down, react, save, follow, more). */
export function VideoActions({ video }: { video: VideoSummary }) {
  return (
    <div className="flex shrink-0 items-center gap-1 max-md:-ml-2">
      <VideoRating id={video.id} />
      {ACTIONS.map((a) => (
        <ActionToggle key={a.list} video={video} {...a} />
      ))}
      {video.creator && (
        <span className="mx-1">
          <FollowButton profile={video.creator} size={34} />
        </span>
      )}
      <MoreMenu path={videoHref(video.id)} title={video.title} align="right" size={42} reportHref={`/takedown?video=${video.id}`} />
    </div>
  );
}

function ActionToggle({ video, list, label, Icon, fill }: (typeof ACTIONS)[number] & { video: VideoSummary }) {
  const [on, toggle] = useVideoToggle(list, video);
  const t = useUi().actions;
  const text = t[on ? label[1] : label[0]];
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      aria-label={text}
      title={text}
      className={cn(
        "grid size-[42px] place-items-center rounded-full transition-colors hover:bg-white/10",
        on ? "text-brand" : "text-soft hover:text-fg",
      )}
    >
      <Icon size={23} strokeWidth={1.6} className={cn("transition-transform active:scale-90", on && fill && "fill-brand")} />
    </button>
  );
}
